import type { InviteOut } from '@/api/client'

/**
 * Recruiter invite funnel, derived client-side from the fields the invites
 * list already returns (created_at / redeemed_at / completed_at) — the
 * aggregate endpoint stays out of scope for v1.
 *
 * Stage semantics:
 * - sent: every invite that could have reached a candidate. A cancelled link
 *   was recalled before anyone saw it, so it never entered the funnel. Today
 *   the only delivery channel is the private link itself; when invite emails
 *   land (#42) this narrows to emailed + copied links, but a never-emailed
 *   invite keeps counting here as link-only — the derivation reads no email
 *   fields, so it cannot break on them.
 * - started: the candidate claimed the link (redeemed_at is stamped by the
 *   atomic claim), whether or not the interview finished.
 * - completed: the report landed (completed_at is stamped alongside it).
 *
 * Every span is epoch-millis math on the ISO stamps, so the UTC timestamps
 * the backend stores need no timezone conversion anywhere.
 */

export interface InviteFunnel {
  sent: number
  started: number
  completed: number
  /** started / sent as a whole percent; null while nothing has been sent. */
  startRate: number | null
  /** completed / started as a whole percent — the stage-to-stage conversion
   *  the arrow between *started* and *completed* actually shows; null while
   *  nothing has started. */
  completeRate: number | null
  /** Median created_at → redeemed_at across started invites; null with no data. */
  medianTimeToStartMs: number | null
  /** Median redeemed_at → completed_at across invites with both stamps; null with no data. */
  medianTimeToCompleteMs: number | null
}

/** Narrow an InviteOut to one that definitely carries stamp `K`, deriving the
 *  non-null type from the source interface so a rename or widening of a stamp
 *  can't leave a stale predicate asserting the old shape. */
type WithStamp<K extends keyof InviteOut> = InviteOut & Record<K, NonNullable<InviteOut[K]>>

const median = (values: number[]): number | null => {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 1 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2)
}

// new Date(bad).getTime() is NaN rather than a throw, and NaN survives the
// zero-clamp below — one unparseable stamp would poison a median ("NaNd") or
// render "expires in NaNh". Treat an unparseable stamp the same as a missing
// one. Values are server-stamped ISO, so this is defensive, not input handling.
const parseStamp = (value: string): number | null => {
  const ms = new Date(value).getTime()
  return Number.isFinite(ms) ? ms : null
}

// Both ends of each span are stamped by the same server clock, but a skewed
// one must not manufacture a negative duration — clamp at zero.
const spanMs = (from: string, to: string) => {
  const start = parseStamp(from)
  const end = parseStamp(to)
  if (start === null || end === null) return 0
  return Math.max(0, end - start)
}

export function buildInviteFunnel(invites: InviteOut[]): InviteFunnel {
  const sent = invites.filter(i => i.status !== 'cancelled')
  const started = sent.filter((i): i is WithStamp<'redeemed_at'> => i.redeemed_at !== null)
  // A completion without a redemption cannot happen through the claim flow;
  // requiring both stamps keeps `completed` from ever widening past `started`
  // and keeps the count and the completion median measuring the same rows.
  const completed = started.filter(
    (i): i is WithStamp<'redeemed_at'> & WithStamp<'completed_at'> => i.completed_at !== null,
  )
  const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : null)
  return {
    sent: sent.length,
    started: started.length,
    completed: completed.length,
    startRate: pct(started.length, sent.length),
    completeRate: pct(completed.length, started.length),
    medianTimeToStartMs: median(started.map(i => spanMs(i.created_at, i.redeemed_at))),
    medianTimeToCompleteMs: median(completed.map(i => spanMs(i.redeemed_at, i.completed_at))),
  }
}

export const EXPIRY_URGENT_WINDOW_MS = 24 * 60 * 60 * 1000

export interface ExpiryUrgency {
  level: 'none' | 'urgent'
  /** Remaining-time countdown like "expires in 6h"; null when nothing to flag. */
  label: string | null
}

/**
 * Awaiting-link urgency: at T-24h the row's quiet "Expires …" date becomes a
 * countdown. Reminders don't exist yet — when #42 adds them, the reminded
 * count appends to the label here ("expires in 6h · reminded once").
 *
 * Shares the `<= 0` expiry boundary with isExpired (dashboard-utils.ts);
 * re-derives it here because the countdown needs the remaining span, not just
 * the boolean.
 */
export function expiryUrgency(expiresAt: string | null, now: number = Date.now()): ExpiryUrgency {
  if (!expiresAt) return { level: 'none', label: null }
  const expires = parseStamp(expiresAt)
  if (expires === null) return { level: 'none', label: null }
  const remainingMs = expires - now
  if (remainingMs <= 0) return { level: 'urgent', label: 'expired' }
  if (remainingMs > EXPIRY_URGENT_WINDOW_MS) return { level: 'none', label: null }
  const mins = Math.floor(remainingMs / 60_000)
  const label =
    mins < 1 ? 'expires in <1m' : mins < 60 ? `expires in ${mins}m` : `expires in ${Math.floor(mins / 60)}h`
  return { level: 'urgent', label }
}

/**
 * Urgency for every invite, keyed by id and computed off a single clock. A
 * caller's summary count and its per-row labels then read the same snapshot, so
 * they can't disagree at the T-24h boundary; each timestamp is also parsed once.
 */
export function urgencyByInvite(
  invites: InviteOut[],
  now: number = Date.now(),
): Map<string, ExpiryUrgency> {
  const map = new Map<string, ExpiryUrgency>()
  for (const invite of invites) map.set(invite.invite_id, expiryUrgency(invite.expires_at, now))
  return map
}
